import { useUserContext } from "../../../users";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ClientProvider from "../../../../modules/ClientProvider";
import { Box, Button, Stack, Typography, Chip } from "@mui/material";
import { Link } from "react-router-dom";
import { CompanyTitle } from "../../components/CompanyTitle";
import { useDealContext } from "../../../deal/context";
import { SchedulerType } from "../../../../types/database/Scheduler";
import { DisplayBoxBase } from "../../../../components/shared/StyledComponents";
import { CalendarDays, Timer, Users } from "lucide-react";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ManageCalendar } from "../../components/calendar/CalendarConfiguration";
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

export const CalendarConfig = ({ setup = false, setCanProceed = () => { }, canProceed = true }: { setup?: boolean, setCanProceed?: (canProceed: boolean) => void, canProceed?: boolean }) => {
    const { dealContext } = useDealContext();
    const { project, organization, role } = dealContext;
    const { userContext } = useUserContext();

    const { getAccessTokenSilently } = useAuth0();
    const [schedulers, setSchedulers] = useState<any | null>(null);
    const projectClient = ClientProvider.provideProjectClient();
    const schedulerClient = ClientProvider.provideSchedulerClient();
    const [selectedSchedulerType, setSelectedSchedulerType] = useState<SchedulerType | null>(null);

    const fetchSchedulerData = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await schedulerClient.getSchedulers(project.tag, accessToken);
        if (error) {
            console.error('Error fetching demo scheduler data:', error);
        } else {
            setSchedulers(data.schedulers);
        }
    };

    useEffect(() => {
        fetchSchedulerData();
        return () => {
            setSchedulers(null);
            setSelectedSchedulerType(null);
        };
    }, []);

    const handleSubmit = async () => {
        fetchSchedulerData();
        setSelectedSchedulerType(null);
    };

    const handleCloseModal = () => {
        setSelectedSchedulerType(null);
    };

    const handleOpenModal = (type: SchedulerType) => {
        setSelectedSchedulerType(type);
    };


    const introScheduler = schedulers?.find((scheduler: any) => scheduler.type === SchedulerType.INTRO);
    const demoScheduler = schedulers?.find((scheduler: any) => scheduler.type === SchedulerType.DEMO);
    const trialScheduler = schedulers?.find((scheduler: any) => scheduler.type === SchedulerType.TRIAL);

    const introStatus = introScheduler?.scheduling_url ? "Configured" : introScheduler?.provider === "custom" && introScheduler?.calendars?.[0]?.url !== "" ? "Pending" : "Not Configured";
    const demoStatus = demoScheduler?.scheduling_url ? "Configured" : demoScheduler?.provider === "custom" && demoScheduler?.calendars?.[0]?.url !== "" ? "Pending" : "Not Configured";
    const trialStatus = trialScheduler?.scheduling_url ? "Configured" : trialScheduler?.provider === "custom" && trialScheduler?.calendars?.[0]?.url !== "" ? "Pending" : "Not Configured";

    return (
        <div>
            <CompanyTitle viewerRole={role} project={project} section="Calendar Setup" subheading={<>Configure your company calendars for the three main types of meetings you'll have with advisors. For more detail on how these meetings fit into the overall lifecycle, <Link to="https://info.brightloop.com/en/articles/11362150-program-overview" target="_blank" rel="noopener noreferrer">click here</Link>.</>} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", marginBottom: "20px" }}>
                <Stack direction="column" spacing={4}>

                    <DisplayBoxBase className="white" sx={{ position: "relative" }}>
                        <Stack direction="column" spacing={2} width="100%">
                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
                                <Box sx={{ position: "relative" }}>
                                    <CalendarDays size={36} />
                                    {introStatus === "Configured" ? (
                                        <CheckCircleIcon
                                            sx={{
                                                position: "absolute",
                                                bottom: -4,
                                                right: -4,
                                                color: "success.main",
                                                fontSize: 20,
                                                backgroundColor: "white",
                                                borderRadius: "50%"
                                            }}
                                        />
                                    ) : introStatus === "Pending" ? (
                                        <TimerOutlinedIcon
                                            sx={{
                                                position: "absolute",
                                                bottom: -4,
                                                right: -4,
                                                color: "warning.main",
                                                fontSize: 20,
                                                backgroundColor: "white",
                                                borderRadius: "50%"
                                            }}
                                        />
                                    ) : (
                                        <HelpOutlineIcon
                                            sx={{
                                                position: "absolute",
                                                bottom: -4,
                                                right: -4,
                                                color: "text.secondary",
                                                fontSize: 20,
                                                backgroundColor: "white",
                                                borderRadius: "50%"
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="body1" fontWeight={600}>{project.effective_scheduler_properties?.intro?.name}</Typography>
                                    <Typography variant="body2">Or Exec Sponsor</Typography>
                                </Box>
                                <Chip
                                    label={introStatus}
                                    color={introStatus === "Configured" ? "success" : introStatus === "Pending" ? "warning" : "default"}
                                    size="small"
                                    sx={{ position: "absolute", top: 16, right: 16 }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body1">
                                    "{project.effective_scheduler_properties?.intro?.description}"
                                </Typography>
                                <Typography variant="body2" mb={2} color="text.secondary">
                                For icebreakers and suggestions for making the most of this meeting, please <Link to="https://info.brightloop.com/en/articles/11362151-meeting-your-advisor" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>click here</Link>.
                                </Typography>
                                <Typography variant="body2" mb={2}>
                                    <strong><CalendarDays size={12} style={{ marginRight: "5px" }}/>Frequency:</strong> One time, when the Advisor signs up
                                    <br />
                                    <strong><Timer size={12} style={{ marginRight: "5px" }}/>Duration:</strong> {project.effective_scheduler_properties?.intro?.duration} minutes
                                    <br />
                                    <strong><Users size={12} style={{ marginRight: "5px" }}/>Attendees:</strong> {project.effective_scheduler_properties?.intro?.host}
                                </Typography>
                            </Box>
                            <Box>
                                <Button
                                    variant={introStatus === "Configured" ? "outlined" : introStatus === "Pending" ? "outlined" : "contained"}
                                    onClick={() => handleOpenModal(SchedulerType.INTRO)}
                                    sx={{ flex: 1 }}
                                >
                                    {introScheduler?.scheduling_url ? "Edit" : introScheduler?.provider === "custom" && introScheduler?.calendars?.[0]?.url !== "" ? "Edit" : "Setup"} Calendar
                                </Button>
                            </Box>
                        </Stack>
                    </DisplayBoxBase>

                    <DisplayBoxBase className="white" sx={{ position: "relative" }}>
                        <Stack direction="column" spacing={2} width="100%">
                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
                                <Box sx={{ position: "relative" }}>
                                    <CalendarDays size={36} />
                                    {demoScheduler?.scheduling_url ? (
                                        <CheckCircleIcon
                                            sx={{
                                                position: "absolute",
                                                bottom: -4,
                                                right: -4,
                                                color: "success.main",
                                                fontSize: 20,
                                                backgroundColor: "white",
                                                borderRadius: "50%"
                                            }}
                                        />
                                    ) : demoStatus === "Pending" ? (
                                        <TimerOutlinedIcon
                                            sx={{
                                                position: "absolute",
                                                bottom: -4,
                                                right: -4,
                                                color: "warning.main",
                                                fontSize: 20,
                                                backgroundColor: "white",
                                                borderRadius: "50%"
                                            }}
                                        />
                                    ) : (
                                        <HelpOutlineIcon
                                            sx={{
                                                position: "absolute",
                                                bottom: -4,
                                                right: -4,
                                                color: "text.secondary",
                                                fontSize: 20,
                                                backgroundColor: "white",
                                                borderRadius: "50%"
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="body1" fontWeight={600}>{project.effective_scheduler_properties?.demo?.name}</Typography>
                                    <Typography variant="body2">With a Product Manager or Sales Engineer</Typography>
                                </Box>
                                <Chip
                                    label={demoStatus}
                                    color={demoStatus === "Configured" ? "success" : demoStatus === "Pending" ? "warning" : "default"}
                                    size="small"
                                    sx={{ position: "absolute", top: 16, right: 16 }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body1">
                                    "{project.effective_scheduler_properties?.demo?.description}"
                                </Typography>
                                <Typography variant="body2" mb={2} color="text.secondary">
                                    For suggestions on getting the most out of this live feedback session, please <Link to="https://info.brightloop.com/en/articles/11362152-product-feedback-session" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>click here</Link>.
                                </Typography>
                                <Typography variant="body2">
                                    <strong><CalendarDays size={12} style={{ marginRight: "5px" }}/>Frequency:</strong> Once when the Advisor signs up, then 2x per year.
                                    <br />
                                    <strong><Timer size={12} style={{ marginRight: "5px" }}/>Duration:</strong> {project.effective_scheduler_properties?.demo?.duration} minutes
                                    <br />
                                    <strong><Users size={12} style={{ marginRight: "5px" }}/>Attendees:</strong> {project.effective_scheduler_properties?.demo?.host}
                                </Typography>
                            </Box>
                            <Box>
                                <Button
                                    variant={demoStatus === "Configured" ? "outlined" : demoStatus === "Pending" ? "outlined" : "contained"}
                                    onClick={() => handleOpenModal(SchedulerType.DEMO)}
                                    sx={{ flex: 1 }}
                                >
                                    {demoScheduler?.scheduling_url ? "Edit" : demoScheduler?.provider === "custom" && demoScheduler?.calendars?.[0]?.url !== "" ? "Edit" : "Setup"} Calendar
                                </Button>
                            </Box>
                        </Stack>
                    </DisplayBoxBase>
                    <DisplayBoxBase className="white" sx={{ position: "relative" }}>
                        <Stack direction="column" spacing={2} width="100%">
                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
                                <Box sx={{ position: "relative" }}>
                                    <CalendarDays size={36} />
                                    {trialScheduler?.scheduling_url ? (
                                        <CheckCircleIcon
                                            sx={{
                                                position: "absolute",
                                                bottom: -4,
                                                right: -4,
                                                color: "success.main",
                                                fontSize: 20,
                                                backgroundColor: "white",
                                                borderRadius: "50%"
                                            }}
                                        />
                                    ) : trialStatus === "Pending" ? (
                                        <TimerOutlinedIcon
                                            sx={{
                                                position: "absolute",
                                                bottom: -4,
                                                right: -4,
                                                color: "warning.main",
                                                fontSize: 20,
                                                backgroundColor: "white",
                                                borderRadius: "50%"
                                            }}
                                        />
                                    ) : (
                                        <HelpOutlineIcon
                                            sx={{
                                                position: "absolute",
                                                bottom: -4,
                                                right: -4,
                                                color: "text.secondary",
                                                fontSize: 20,
                                                backgroundColor: "white",
                                                borderRadius: "50%"
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="body1" fontWeight={600}>{project.effective_scheduler_properties?.trial?.name}</Typography>
                                    <Typography variant="body2">With the Sales Team</Typography>
                                </Box>
                                <Chip
                                    label={trialStatus}
                                    color={trialStatus === "Configured" ? "success" : trialStatus === "Pending" ? "warning" : "default"}
                                    size="small"
                                    sx={{ position: "absolute", top: 16, right: 16 }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body1">    
                                    "{project.effective_scheduler_properties?.trial?.description}"
                                </Typography>
                                <Typography variant="body2" mb={2} color="text.secondary">
                                    This is a pure sales meeting. See <Link to="https://info.brightloop.com/en/articles/11362165-trial-exploration" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>here</Link> for suggestions for making the most of it.
                                </Typography>
                                <Typography variant="body2">
                                    <strong><CalendarDays size={12} style={{ marginRight: "5px" }}/>Frequency:</strong> When requested by the Advisor
                                    <br />
                                    <strong><Timer size={12} style={{ marginRight: "5px" }}/>Duration:</strong> {project.effective_scheduler_properties?.trial?.duration} minutes
                                    <br />
                                    <strong><Users size={12} style={{ marginRight: "5px" }}/>Attendees:</strong> {project.effective_scheduler_properties?.trial?.host}
                                </Typography>
                            </Box>
                            <Box>
                                <Button
                                    variant={trialStatus === "Configured" ? "outlined" : trialStatus === "Pending" ? "outlined" : "contained"}
                                    onClick={() => handleOpenModal(SchedulerType.TRIAL)}
                                    sx={{ flex: 1 }}
                                >
                                    {trialScheduler?.scheduling_url ? "Edit" : trialScheduler?.provider === "custom" && trialScheduler?.calendars?.[0]?.url !== "" ? "Edit" : "Setup"} Calendar
                                </Button>
                            </Box>
                        </Stack>
                    </DisplayBoxBase>
                </Stack>

                {selectedSchedulerType && (
                    <ManageCalendar
                        open={selectedSchedulerType !== null}
                        onClose={handleCloseModal}
                        onSubmit={handleSubmit}
                        schedulerType={selectedSchedulerType}
                    />
                )}
            </Box>
        </div>
    );
}